import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import { TextInput } from '@tassomutuo-backoffice/ui';
import { KeyPeople, ShareHolders } from '@tassomutuo-backoffice/types';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

interface IFormInputs {
  company_info: {
    group: string;
    official_website: string;
    founded: string;
    employees: string;
    headquarters: string;
    branches_in_italy: string;
    key_people: KeyPeople[];
  };
  financial_info: {
    revenue: string;
    net_profit: string;
    shareholders: ShareHolders[];
  };
  contact_info: {
    email: string;
    phone: string;
  };
}

interface IProps {
  disabled?: boolean;
}

const BankInfoInputsForm = ({ disabled = false }: IProps) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<IFormInputs>();

  // for key_people
  const {
    fields: kpFields,
    append: appendKP,
    remove: removeKP,
  } = useFieldArray({
    control,
    name: 'company_info.key_people',
  });

  // for shareholders
  const {
    fields: shFields,
    append: appendSH,
    remove: removeSH,
  } = useFieldArray({
    control,
    name: 'financial_info.shareholders',
  });

  console.log(watch());
  return (
    <Box width="100%">
      <Box display="flex" flexDirection={{md:"column",lg:"row"}} gap={4}>
        <Box flex={1}>
          <Typography variant="h5" mb={2}>
            Informazioni sull'azienda
          </Typography>

          <Controller
            control={control}
            name={'company_info.group'}
            rules={{
              required: 'Campo obbligatorio',
            }}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Gruppo"
                helperText={errors?.company_info?.group?.message}
                error={!!errors?.company_info?.group}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={'company_info.official_website'}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Sito Ufficiale"
                helperText={errors?.company_info?.official_website?.message}
                error={!!errors?.company_info?.official_website}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={'company_info.founded'}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Fondazione"
                helperText={errors?.company_info?.founded?.message}
                error={!!errors?.company_info?.founded}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={'company_info.employees'}
            disabled={disabled}
            defaultValue={undefined}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Dipendenti"
                helperText={errors?.company_info?.employees?.message}
                error={!!errors?.company_info?.employees}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={'company_info.headquarters'}
            disabled={disabled}
            defaultValue={undefined}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Sede Principale"
                helperText={errors?.company_info?.headquarters?.message}
                error={!!errors?.company_info?.headquarters}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name={'company_info.branches_in_italy'}
            disabled={disabled}
            defaultValue={undefined}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Presenza nel territorio"
                helperText={errors?.company_info?.branches_in_italy?.message}
                error={!!errors?.company_info?.branches_in_italy}
                {...field}
              />
            )}
          />
          <Typography variant="body2" my={2}>
            Persone Chiave
          </Typography>
          {kpFields.map((field, idx) => (
            <Box
              key={field.id}
              display="flex"
              justifyContent={'center'}
              gap={2}
              mb={2}
              alignItems="flex-end"
            >
              <Controller
                name={`company_info.key_people.${idx}.name`}
                control={control}
                defaultValue={field.name}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Nome"
                    error={!!errors.company_info?.key_people?.[idx]?.name}
                    helperText={
                      errors.company_info?.key_people?.[idx]?.name?.message
                    }
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name={`company_info.key_people.${idx}.role`}
                control={control}
                defaultValue={field.role}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Ruolo"
                    error={!!errors.company_info?.key_people?.[idx]?.role}
                    helperText={
                      errors.company_info?.key_people?.[idx]?.role?.message
                    }
                    disabled={disabled}
                  />
                )}
              />
              <Button
                sx={{ my: '12px' }}
                variant="text"
                color="error"
                onClick={() => removeKP(idx)}
                disabled={disabled}
              >
                <DeleteIcon />
              </Button>
            </Box>
          ))}
          <Button
            onClick={() => appendKP({ name: '', role: '' })}
            disabled={disabled}
            variant="contained"
            sx={{ justifyContent: 'space-between', gap: 1 }}
          >
            <AddIcon />
            Aggiungi persone chiave
          </Button>
        </Box>

        <Box flex={1}>
          <Typography variant="h5" mb={2}>
            Informazioni finanziarie
          </Typography>

          <Controller
            control={control}
            name={'financial_info.revenue'}
            // rules={{
            //   required: 'Campo obbligatorio',
            // }}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Fatturato"
                helperText={errors?.financial_info?.revenue?.message}
                error={!!errors?.financial_info?.revenue}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name={'financial_info.net_profit'}
            // rules={{
            //   required: 'Campo obbligatorio',
            // }}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Utile Netto"
                helperText={errors?.financial_info?.net_profit?.message}
                error={!!errors?.financial_info?.net_profit}
                {...field}
              />
            )}
          />
          <Typography variant="body2" my={2}>
            Azionisti
          </Typography>
          {shFields.map((field, idx) => (
            <Box
              key={field.id}
              display="flex"
              justifyContent={'center'}
              gap={2}
              mb={2}
              alignItems="flex-end"
            >
              <Controller
                name={`financial_info.shareholders.${idx}.name`}
                control={control}
                defaultValue={field.name}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Nome"
                    type="text"
                    error={!!errors.financial_info?.shareholders?.[idx]?.name}
                    helperText={
                      errors.financial_info?.shareholders?.[idx]?.name?.message
                    }
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name={`financial_info.shareholders.${idx}.percentage`}
                control={control}
                defaultValue={field.percentage}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Percentuale"
                    type="text"
                    error={
                      !!errors.financial_info?.shareholders?.[idx]?.percentage
                    }
                    helperText={
                      errors.financial_info?.shareholders?.[idx]?.percentage
                        ?.message
                    }
                    InputProps={{
                      min: 0,
                      max: 100,
                    }}
                    disabled={disabled}
                  />
                )}
              />
              <Button
                sx={{ my: '12px' }}
                variant="text"
                color="error"
                onClick={() => removeSH(idx)}
                disabled={disabled}
              >
                <DeleteIcon />
              </Button>
            </Box>
          ))}
          <Button
            onClick={() => appendSH({ name: '', percentage: '' })}
            disabled={disabled}
            variant="contained"
            sx={{ justifyContent: 'space-between', gap: 1 }}
          >
            <AddIcon />
            Aggiungi azionista
          </Button>
        </Box>

        <Box flex={1}>
          <Typography variant="h5" mb={2}>
            Informazioni di contatto
          </Typography>

          <Controller
            control={control}
            name={'contact_info.email'}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Email"
                helperText={errors?.contact_info?.email?.message}
                error={!!errors?.contact_info?.email}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name={'contact_info.phone'}
            disabled={disabled}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Telefono"
                helperText={errors?.contact_info?.phone?.message}
                error={!!errors?.contact_info?.phone}
                {...field}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};
export default BankInfoInputsForm;
